import React, { useContext, useMemo } from 'react';
import { TippyTooltip } from '@bbkAdminComponents/tooltips/tippy-tooltip';
import icons from '@bbkAdminUtils/icons';
import { cs } from '@bbkAdminUtils/css-utils';
import {
  ADD_OFFER_VALUE,
  PlayContext,
  PlayObjectProperty,
  PlayTargetType,
  PlayValidationErrorType,
} from '@bbkAdminComponents/play/play-context';
import GenericSelect from '@bbkAdminComponents/select/select';
import { produce } from 'immer';
import _ from 'lodash';
import { PercentToTargetType } from '@bbkAdminUtils/api-client/routing.types';
import { appPersistSlice } from '@bbkAdminRedux/rtkq/app-persist/app-persist.slice';
import { useAppDispatch } from '@bbkAdminRedux/redux_utils';
import { useSelector } from 'react-redux';
import { selectAppId } from '@bbkAdminRedux/app/selectors';
import { BlackToastTrigger } from '@bbkAdminComponents/alerts/black-toast';

type Props = {
  componentTitle: string;
  multipleOfferPages?: boolean;
};

export const PlayControlSelect: React.FC<Props> = (props: Props) => {
  const {
    play,
    getPlayObjectProperty,
    setPlayObjectProperty,
    offerOptions,
    handleCreateNewOffer,
    resetControlSelection,
    getActiveTarget,
    setPlayValidationErrors,
    playValidationErrors,
  } = useContext(PlayContext);
  const dispatch = useAppDispatch();
  const appKey = useSelector(selectAppId);

  const controlNode = getPlayObjectProperty(
    PlayObjectProperty.CONTROL_NODE
  ) as PercentToTargetType;

  const controlPercent = getPlayObjectProperty(
    PlayObjectProperty.CONTROL_PERCENTAGE
  );

  const offerPercent = getPlayObjectProperty(
    PlayObjectProperty.OFFER_PERCENTAGE
  );

  const controlTarget = getPlayObjectProperty(
    PlayObjectProperty.CONTROL_TARGET
  );

  const setAddNewOfferPath = () => {
    dispatch(
      appPersistSlice.actions.saveAppMisc({
        appKey,
        playState: {
          play: play,
          addNewOffer: {
            path: ['draft_targeting', 'routing_percents', 0, 'target'],
            value: undefined,
          },
        },
      })
    );
  };

  const offerOptionsWithNoOffer = useMemo(() => {
    return [...offerOptions, { value: 'none', label: 'No offer' }];
  }, [offerOptions]);

  const isInvalidControlPercentage = useMemo(() => {
    return _.get(
      playValidationErrors,
      [PlayValidationErrorType.Offer, 'isInvalidControlPercentage'],
      false
    );
  }, [playValidationErrors]);

  return (
    <div>
      <div className="tw-mb-2 tw-mt-4 tw-w-full tw-rounded-lg tw-bg-cbrneutral-50 tw-p-6">
        <div>
          <div className="tw-flex tw-w-full tw-justify-between">
            <div className="tw-flex tw-items-center">
              <span className="tw-font-semibold">Control</span>
              <TippyTooltip content="Maintain a baseline to track changes in your play performance over time.">
                <span className="info-icon tw-ml-2 tw-flex tw-items-center">
                  {icons.cbrInfo}
                </span>
              </TippyTooltip>
            </div>
            <button
              className={cs(
                'tw-flex tw-cursor-pointer tw-items-center tw-text-sm tw-font-bold tw-text-[#E65051]'
              )}
              onClick={resetControlSelection}
            >
              <span className="tw-mr-1 tw-flex tw-items-center">
                {icons.minusCircle}
              </span>{' '}
              Remove
            </button>
          </div>
        </div>
        <div>
          <div className="tw-flex tw-items-center">
            Show this offer
            <span className="flex-1 tw-mx-2">
              <GenericSelect
                options={offerOptionsWithNoOffer}
                value={offerOptionsWithNoOffer.find(
                  (r) => r.value === controlTarget
                )}
                key={controlTarget}
                onChange={(e) => {
                  if (e.value === ADD_OFFER_VALUE) {
                    setAddNewOfferPath();
                    handleCreateNewOffer();
                    return;
                  }

                  let value = {
                    type: PlayTargetType.NONE,
                    target_key: 'none',
                  };

                  if (e.value != 'none') {
                    value = {
                      type: getActiveTarget(),
                      target_key: e.value,
                    };
                  }

                  const updatedNode = produce(controlNode, (draft) => {
                    _.set(draft, ['target'], value);
                  });
                  setPlayObjectProperty(
                    PlayObjectProperty.CONTROL_NODE,
                    updatedNode
                  );
                }}
              />
            </span>
            to
            <span className="tw-ml-2">
              <div className="tw-flex tw-items-center">
                <input
                  className={cs('bbk-simple-text-input tw-min-w-24 !tw-pr-6', {
                    'tw-border tw-border-solid !tw-border-cbrdanger-400':
                      isInvalidControlPercentage,
                  })}
                  type="number"
                  value={controlPercent}
                  min={0}
                  max={99}
                  onChange={(e) => {
                    const value = parseInt(e.currentTarget.value, 10);
                    let isInvalidControlPercent = false;
                    if (value < 0 || value > 99 || isNaN(value)) {
                      isInvalidControlPercent = true;
                    } else if (value === 0) {
                      BlackToastTrigger({ content: 'Control has been reset' });
                    }
                    setPlayValidationErrors((prev) => {
                      const updatedValidationErrors = produce(prev, (draft) => {
                        _.set(
                          draft,
                          [
                            PlayValidationErrorType.Offer,
                            'isInvalidControlPercentage',
                          ],
                          isInvalidControlPercent
                        );
                      });
                      return updatedValidationErrors;
                    });
                    setPlayObjectProperty(
                      PlayObjectProperty.CONTROL_PERCENTAGE,
                      value
                    );
                    if (
                      getPlayObjectProperty(PlayObjectProperty.OFFER_PERCENTAGE)
                    ) {
                      setPlayObjectProperty(
                        PlayObjectProperty.OFFER_PERCENTAGE,
                        isInvalidControlPercent ? 100 : 100 - value
                      );
                    }
                  }}
                />
                <span className="-tw-translate-x-5">%</span>
              </div>
            </span>
            of all traffic.
          </div>
        </div>
      </div>
      {isInvalidControlPercentage && (
        <span className="tw-mt-2 tw-text-sm tw-text-cbrdanger-400">
          Control percentage is invalid, value must be between 0 and 99
        </span>
      )}
      <div className="tw-mv-4 tw-mb-6 tw-mt-4">
        <span className="tw-text-lg tw-font-bold">{props.componentTitle}</span>
        <div className="tw-flex tw-items-center">
          {props.multipleOfferPages
            ? 'Show these offer pages to the remaining'
            : 'Show this offer page to the remaining'}
          &nbsp;
          <span className="tw-font-semibold">{`${offerPercent}%`}</span>
          &nbsp;of all the traffic
        </div>
      </div>
    </div>
  );
};
